import React, { ImgHTMLAttributes } from 'react';
import styled from '@emotion/styled';
import css from '@styled-system/css';
import PropTypes from 'prop-types';
import { system, variant } from 'styled-system';

import { responsiveType } from '../../internal/propTypeValidators';
import sx from '../../internal/styles/sx';
import { IMAGE_SIZES, ImageProps } from './types';

const StyledImage = styled('img')<ImageProps>(
  css({
    width: 0,
  }),
  ({ bordered }) =>
    css(
      bordered
        ? {
            border: `1px solid`,
          }
        : {}
    ),
  ({ inline }) => ({ display: inline ? undefined : 'block' }),
  system({
    rounded: {
      property: 'borderRadius',
      transform: (value) => value && `50%`,
    },
  }),
  system({
    borderColor: {
      property: 'borderColor',
      scale: 'colors',
    },
  }),
  variant({
    prop: 'size',
    variants: {
      [IMAGE_SIZES.SMALL]: {
        width: 16,
      },
      [IMAGE_SIZES.MEDIUM]: {
        width: 24,
      },
      [IMAGE_SIZES.LARGE]: {
        width: 32,
      },
      [IMAGE_SIZES.AUTO]: {
        width: 'auto',
      },
    },
  }),
  sx
);

const Image = React.forwardRef<
  HTMLImageElement,
  React.ComponentProps<typeof StyledImage>
>(
  (
    {
      size = IMAGE_SIZES.SMALL,
      inline = true,
      borderColor = 'neutral.40',
      ...rest
    },
    ref
  ) => (
    <StyledImage
      ref={ref}
      size={size}
      inline={inline}
      borderColor={borderColor}
      {...rest}
    />
  )
);

Image.propTypes = {
  alt: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
  bordered: PropTypes.bool,
  borderColor: responsiveType(PropTypes.string),
  size: PropTypes.oneOf(Object.values(IMAGE_SIZES)),
  inline: PropTypes.bool,
  sx: PropTypes.object,
};

export default Image;
